<template>
  <div ref="root" class="membership-sidebar-root">
    <div
      v-show="isFloating"
      class="membership-sidebar-spacer"
      :style="{ height: spacerHeight + 'px' }"
      aria-hidden="true"
    />
    <aside
      ref="panel"
      class="membership-sidebar"
      :class="{ 'is-floating': isFloating }"
      :style="panelStyle"
      aria-label="会员信息"
    >
      <article class="membership-sidebar-card membership-sidebar-profile">
        <div class="membership-sidebar-profile__head">
          <span class="membership-sidebar-avatar" aria-hidden="true">{{ avatarLetter }}</span>
          <div class="membership-sidebar-profile__meta">
            <strong>{{ displayName }}</strong>
            <span class="membership-sidebar-org">中国古生物学会</span>
            <span class="membership-sidebar-pill" :class="statusPillClass">{{ statusPillText }}</span>
          </div>
        </div>
        <dl class="membership-sidebar-fields">
          <div>
            <dt>姓名</dt>
            <dd>{{ displayName }}</dd>
          </div>
          <div>
            <dt>单位</dt>
            <dd>{{ displayUnit }}</dd>
          </div>
          <div>
            <dt>电子邮箱</dt>
            <dd>{{ displayEmail }}</dd>
          </div>
          <div v-if="showMemberNo">
            <dt>会员号</dt>
            <dd>{{ front.memberNoDisplay }}</dd>
          </div>
        </dl>
        <router-link
          v-if="front.isLoggedIn"
          class="membership-sidebar-link"
          :to="{ path: '/personal-center', query: { section: 'account' } }"
        >
          完善资料
        </router-link>
        <p v-else class="membership-sidebar-hint">登录后可查看完整资料</p>
      </article>

      <article class="membership-sidebar-card membership-sidebar-status">
        <header class="membership-sidebar-status__head">
          <span class="membership-sidebar-status__ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3.5" y="4" width="17" height="12.5" rx="1.6" stroke="currentColor" stroke-width="1.7"/>
              <path d="M8 20h8M12 16.5V20" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
            </svg>
          </span>
          <h3>学会会员状态</h3>
        </header>
        <template v-if="!front.isLoggedIn">
          <p class="membership-sidebar-hint">请先登录后查看会员状态。</p>
        </template>
        <template v-else-if="!front.membershipSessionSynced">
          <p class="membership-sidebar-hint">正在同步会员身份…</p>
        </template>
        <template v-else-if="front.needsMembershipChoice">
          <p class="membership-sidebar-hint">请先在右侧选择参与方式。</p>
        </template>
        <template v-else>
          <div
            class="membership-sidebar-status__body"
            :class="{
              'is-expired': front.isExpiredMember,
              'is-active': front.isActiveMember && !front.isExpiredMember,
              'is-muted': !front.isActiveMember && !front.isExpiredMember
            }"
          >
            <span class="membership-sidebar-status__badge">{{ statusBadge }}</span>
            <span v-if="showStatusLabel" class="membership-sidebar-status__label">{{ front.membershipStatusLabel }}</span>
            <span v-if="front.isPermanentMember" class="membership-sidebar-status__date">长期有效</span>
            <span v-else-if="front.memberValidEndDate" class="membership-sidebar-status__date">
              {{ front.isExpiredMember ? '过期于' : '有效期至' }}
              {{ front.formatDate(front.memberValidEndDate) }}
              <template v-if="front.isNearExpiry && !front.isExpiredMember">
                （剩余 {{ front.daysUntilExpiry }} 天）
              </template>
            </span>
            <p v-if="statusHint" class="membership-sidebar-status__hint">{{ statusHint }}</p>
          </div>
        </template>
      </article>
    </aside>
  </div>
</template>

<script>
const FLOAT_TOP = 148
const FLOAT_BOTTOM_GAP = 20
const FLOAT_MQ = '(max-width: 900px)'

export default {
  name: 'MembershipSidebar',
  inject: ['front'],
  data() {
    return {
      isFloating: false,
      spacerHeight: 0,
      panelStyle: {},
      rafId: 0
    }
  },
  computed: {
    displayName() {
      if (!this.front.isLoggedIn) return '未登录'
      const form = this.front.profileForm || {}
      return form.userName || this.front.userNickname || '账号'
    },
    displayUnit() {
      if (!this.front.isLoggedIn) return '—'
      const form = this.front.profileForm || {}
      const unit = (form.unit || '').trim()
      return unit || '未填写'
    },
    displayEmail() {
      if (!this.front.isLoggedIn) return '—'
      const form = this.front.profileForm || {}
      const email = (form.email || (this.front.userInfo && this.front.userInfo.email) || '').trim()
      return email || '未填写'
    },
    avatarLetter() {
      const name = this.displayName
      return name && name !== '未登录' ? String(name).charAt(0) : '?'
    },
    showMemberNo() {
      return this.front.isLoggedIn
        && this.front.memberNoDisplay
        && this.front.memberNoDisplay !== '暂无'
    },
    statusPillText() {
      if (!this.front.isLoggedIn) return '未登录'
      if (this.front.needsMembershipChoice) return '待选择'
      if (this.front.isActiveMember) return this.front.memberTypeDisplayLabel || '正式会员'
      if (this.front.organizationBindingStatus === 'ACTIVE' && this.front.organizationMemberBenefit) return '单位会员关联用户'
      if (this.front.organizationBindingStatus === 'ACTIVE') return '单位权益已暂停'
      if (this.front.organizationBindingStatus === 'PENDING') return '单位关联待确认'
      if (this.front.organizationBindingStatus === 'REJECTED') return '单位关联未通过'
      if (this.front.isExpiredMember) return '已过期'
      if (this.front.userInfo && this.front.userInfo.userType === 'non_member') return '非会员'
      return this.front.membershipStatusLabel || '办理中'
    },
    statusPillClass() {
      if (!this.front.isLoggedIn || this.front.needsMembershipChoice) return 'is-muted'
      if (this.front.isActiveMember) return 'is-ok'
      if (this.front.organizationBindingStatus === 'ACTIVE' && this.front.organizationMemberBenefit) return 'is-ok'
      if (this.front.isExpiredMember) return 'is-warn'
      return 'is-muted'
    },
    statusBadge() {
      if (this.front.isActiveMember) return this.front.memberTypeDisplayLabel || '正式会员'
      if (this.front.organizationBindingStatus === 'ACTIVE' && this.front.organizationMemberBenefit) return '单位会员关联用户'
      if (this.front.organizationBindingStatus === 'ACTIVE') return '单位权益已暂停'
      if (this.front.organizationBindingStatus === 'PENDING') return '单位关联待确认'
      if (this.front.organizationBindingStatus === 'REJECTED') return '单位关联未通过'
      if (this.front.isExpiredMember) return '已过期'
      if (this.front.userInfo && this.front.userInfo.userType === 'non_member') return '非会员'
      return this.front.membershipStatusLabel || '办理中'
    },
    showStatusLabel() {
      const label = String(this.front.membershipStatusLabel || '').trim()
      const badge = String(this.statusBadge || '').trim()
      return Boolean(label && label !== badge)
    },
    statusHint() {
      if (this.front.organizationBindingStatus === 'ACTIVE' && this.front.organizationMemberBenefit) {
        return `已关联“${this.front.organizationMembershipName}”，可在单位有效期内享受会议会员权益。`
      }
      if (this.front.organizationBindingStatus === 'ACTIVE') {
        return `已关联“${this.front.organizationMembershipName}”，但单位会员当前已到期、已注销或尚未生效，请联系秘书处。`
      }
      if (this.front.organizationBindingStatus === 'PENDING') {
        return `已申请关联“${this.front.organizationMembershipName}”，请等待秘书处确认。`
      }
      if (this.front.organizationBindingStatus === 'REJECTED') {
        return this.front.organizationBindingReviewComment || '单位会员关联未通过，请联系秘书处核对。'
      }
      if (this.front.isExpiredMember) {
        return '会员资格已到期，续费后即可恢复正式会员身份。'
      }
      if (this.front.isNearExpiry && !this.front.isPermanentMember && !this.front.isCouncilMemberPath) {
        return `会员即将到期（剩余 ${this.front.daysUntilExpiry} 天），请及时续费。`
      }
      if (this.front.canUpgradeToMemberPath) {
        return '可升级为正式会员后办理入会与缴费。'
      }
      return ''
    }
  },
  mounted() {
    this.scheduleFloating()
    window.addEventListener('scroll', this.scheduleFloating, { passive: true })
    window.addEventListener('resize', this.scheduleFloating)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scheduleFloating)
    window.removeEventListener('resize', this.scheduleFloating)
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = 0
    }
  },
  methods: {
    scheduleFloating() {
      if (this.rafId) return
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0
        this.updateFloating()
      })
    },
    resetFloating() {
      this.isFloating = false
      this.spacerHeight = 0
      this.panelStyle = {}
    },
    updateFloating() {
      if (typeof window === 'undefined' || window.matchMedia(FLOAT_MQ).matches) {
        this.resetFloating()
        return
      }
      const root = this.$refs.root
      const panel = this.$refs.panel
      if (!root || !panel) {
        this.resetFloating()
        return
      }
      const slot = root.closest('.membership-sidebar-slot') || root.parentElement
      const layout = root.closest('.member-service-layout')
      if (!slot || !layout) {
        this.resetFloating()
        return
      }

      const slotRect = slot.getBoundingClientRect()
      const layoutRect = layout.getBoundingClientRect()
      const panelHeight = panel.offsetHeight || 0
      if (!panelHeight || layoutRect.bottom <= FLOAT_TOP) {
        this.resetFloating()
        return
      }

      // 尚未滚过侧栏自然位置：保持文档流
      if (slotRect.top > FLOAT_TOP) {
        this.resetFloating()
        return
      }

      let top = FLOAT_TOP
      const maxTop = layoutRect.bottom - panelHeight - FLOAT_BOTTOM_GAP
      if (maxTop < FLOAT_TOP) {
        top = Math.max(12, maxTop)
      }

      this.isFloating = true
      this.spacerHeight = panelHeight
      this.panelStyle = {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(slotRect.left)}px`,
        width: `${Math.round(slotRect.width)}px`,
        zIndex: 30
      }
    }
  }
}
</script>

<style scoped>
.membership-sidebar-root {
  position: relative;
  min-width: 0;
}

.membership-sidebar-spacer {
  width: 100%;
  pointer-events: none;
}

.membership-sidebar {
  display: grid;
  gap: 16px;
  align-content: start;
}

.membership-sidebar.is-floating {
  box-sizing: border-box;
}

.membership-sidebar-card {
  padding: 18px;
  border: 1px solid rgba(31, 79, 122, 0.1);
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(14, 38, 67, 0.07);
}

.membership-sidebar-profile__head {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 0;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(31, 79, 122, 0.08);
}

.membership-sidebar-avatar {
  display: grid;
  width: 56px;
  height: 56px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 50%;
  background: linear-gradient(160deg, #003d6b 0%, #002b49 100%);
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  box-shadow: 0 0 0 3px #fff, 0 0 0 5px rgba(0, 43, 73, 0.12);
}

.membership-sidebar-profile__meta {
  display: grid;
  gap: 5px;
  min-width: 0;
  padding-top: 2px;
}

.membership-sidebar-profile__meta strong {
  color: var(--primary, #002b49);
  font-size: 18px;
  line-height: 1.25;
  letter-spacing: 0.01em;
}

.membership-sidebar-org {
  color: var(--subtle, #5f6670);
  font-size: 13px;
  line-height: 1.45;
}

.membership-sidebar-pill {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  margin-top: 2px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
}

.membership-sidebar-pill.is-ok {
  background: #e8f7ee;
  color: #147a3a;
}

.membership-sidebar-pill.is-warn {
  background: #fef3c7;
  color: #92400e;
}

.membership-sidebar-pill.is-muted {
  background: #eef2f6;
  color: #5f6670;
}

.membership-sidebar-fields {
  display: grid;
  gap: 0;
  margin: 12px 0 14px;
  border-radius: 8px;
  border: 1px solid rgba(31, 79, 122, 0.08);
  background: #f8fafc;
  overflow: hidden;
}

.membership-sidebar-fields > div {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.55;
  border-bottom: 1px solid rgba(31, 79, 122, 0.06);
}

.membership-sidebar-fields > div:last-child {
  border-bottom: 0;
}

.membership-sidebar-fields dt {
  margin: 0;
  flex: 0 0 auto;
  color: var(--subtle, #5f6670);
  font-weight: 600;
}

.membership-sidebar-fields dd {
  margin: 0;
  min-width: 0;
  color: var(--text, #1a1c1e);
  text-align: right;
  font-weight: 600;
  word-break: break-all;
}

.membership-sidebar-link {
  display: inline-flex;
  align-items: center;
  color: var(--primary, #002b49);
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
}

.membership-sidebar-link:hover {
  text-decoration: underline;
}

.membership-sidebar-hint {
  margin: 0;
  color: var(--subtle, #5f6670);
  font-size: 13px;
  line-height: 1.55;
}

.membership-sidebar-status__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.membership-sidebar-status__head h3 {
  margin: 0;
  color: var(--primary, #002b49);
  font-size: 16px;
  font-weight: 800;
}

.membership-sidebar-status__ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(0, 43, 73, 0.06);
  color: var(--primary, #002b49);
  line-height: 1;
}

.membership-sidebar-status__body {
  display: grid;
  gap: 8px;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid #cfe8d5;
  background: linear-gradient(180deg, #f5fbf7 0%, #eef8f1 100%);
}

.membership-sidebar-status__body.is-expired {
  border-color: #e5e7eb;
  background: #f9fafb;
}

.membership-sidebar-status__body.is-muted {
  border-color: rgba(31, 79, 122, 0.12);
  background: linear-gradient(180deg, #f8fbff 0%, #f2f6fa 100%);
}

.membership-sidebar-status__badge {
  display: inline-flex;
  width: fit-content;
  padding: 3px 11px;
  border-radius: 999px;
  background: #dcfce7;
  color: #147a3a;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.55;
}

.membership-sidebar-status__body.is-expired .membership-sidebar-status__badge {
  background: #e5e7eb;
  color: #6b7280;
}

.membership-sidebar-status__body.is-muted .membership-sidebar-status__badge {
  background: #e8eef5;
  color: #3d5672;
}

.membership-sidebar-status__label {
  color: var(--primary, #002b49);
  font-size: 14px;
  font-weight: 700;
}

.membership-sidebar-status__date {
  color: var(--subtle, #5f6670);
  font-size: 13px;
}

.membership-sidebar-status__hint {
  margin: 2px 0 0;
  color: var(--subtle, #5f6670);
  font-size: 13px;
  line-height: 1.5;
}

.membership-sidebar-status__body.is-expired .membership-sidebar-status__hint {
  color: #6b7280;
}
</style>
